import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { CargoListService } from '@/services/cargoListService';
import { getWriteErrorMessage } from '@/utils/apiError';
import { CARGO_SEGMENT_FILTER_ALL } from '@/constants/cargoList.constants';
import type { CargoListState, CargoListFilters, UpdateCargoRequest } from '@/types/cargoList.types';

/**
 * Record a failed write and hand back an error for the caller to rethrow.
 *
 * Write actions must reject rather than resolve quietly: the pages await them and
 * then navigate away or close a modal on success, so swallowing the error here
 * would report a delete or a save that never happened.
 */
function failWrite(
  set: (partial: { error: string; loading: false }) => void,
  error: unknown,
  fallback: string
): Error {
  const message = getWriteErrorMessage(error, fallback);
  set({ error: message, loading: false });
  return new Error(message);
}

const initialFilters: CargoListFilters = {
  searchQuery: '',
  segmentFilter: CARGO_SEGMENT_FILTER_ALL,
};

interface CargoListStore extends CargoListState {
  // Actions
  fetchCargoList: () => Promise<void>;
  refreshCargoList: () => Promise<void>;
  updateCargo: (id: number, data: UpdateCargoRequest) => Promise<void>;
  deleteCargo: (id: number) => Promise<void>;
  updateFilters: (filters: Partial<CargoListFilters>) => void;
}

export const useCargoListStore = create<CargoListStore>()(
  devtools(
    (set, get) => ({
      // Initial state
      items: [],
      loading: false,
      error: null,
      refreshing: false,
      usingMockData: false,
      filters: initialFilters,

      // Actions
      fetchCargoList: async () => {
        set({ loading: true, error: null });
        try {
          const items = await CargoListService.getCargoList();
          set({ items, loading: false, usingMockData: CargoListService.usingMockData });
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Failed to fetch cargo list';
          set({ error: errorMessage, loading: false });
        }
      },

      refreshCargoList: async () => {
        set({ refreshing: true, error: null });

        try {
          const items = await CargoListService.getCargoList();
          set({ items, refreshing: false, usingMockData: CargoListService.usingMockData });
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Failed to refresh cargo list';
          set({ error: errorMessage, refreshing: false });
        }
      },

      updateCargo: async (id: number, data: UpdateCargoRequest) => {
        set({ loading: true, error: null });

        try {
          await CargoListService.updateCargoItem(id, data);
        } catch (error) {
          throw failWrite(set, error, 'Failed to update cargo');
        }

        // The save has already succeeded past this point, so a failure here must
        // not be reported as a failed update. The API answers a successful PUT
        // with 204 No Content, so there is no entity in the response to merge -
        // re-read the row instead, for the server-computed fields
        // (updatedTime/updatedBy) and the display names behind the ids we sent.
        // If that read fails the row is simply left stale until the next refresh.
        try {
          const updatedCargo = await CargoListService.getCargoItem(id);
          const { items } = get();
          set({
            items: items.map((item) => (item.id === id ? updatedCargo : item)),
            loading: false,
          });
        } catch {
          set({ loading: false });
        }
      },

      deleteCargo: async (id: number) => {
        set({ loading: true, error: null });

        try {
          await CargoListService.deleteCargoItem(id);
          const { items } = get();
          set({ items: items.filter((item) => item.id !== id), loading: false });
        } catch (error) {
          throw failWrite(set, error, 'Failed to delete cargo');
        }
      },

      updateFilters: (newFilters: Partial<CargoListFilters>) => {
        const { filters } = get();
        set({
          filters: { ...filters, ...newFilters },
        });
      },
    }),
    { name: 'CargoListStore' }
  )
);
